/**
 * Conversations API client (BFF: `/api/v1/conversations/**`, tag *Conversations*, and the
 * Customer 360 shortcut `/api/v1/customers/{customerId}/conversation`, tag *Customer Conversation*).
 *
 * 1:1 with `web-merchant-portal-bff`'s `openapi.yml` §13d/§13e as shipped on 2026-09-17 — the BFF is
 * a pass-through to web-customer-assistant-service (the hub) and does not re-sort or re-shape.
 * Reads need `conversations.view`; every mutation needs `conversations.manage`; an operator reply
 * additionally needs `conversations.feature.operator-reply`; unmasked customer-typed content needs
 * `customers.feature.pii-view` (forwarded downstream as `X-Pii-View`). See adr-merchant-portal-021.
 */
import { apiClient, apiConfig } from '@/shared/api';

import type {
  CloseConversationRequest,
  ConversationDetail,
  ConversationEpisodePage,
  ConversationEpisodeSummary,
  ConversationEscalation,
  ConversationMessagePage,
  ConversationPage,
  ListConversationEpisodesParams,
  ListConversationMessagesParams,
  ListConversationsParams,
  OperatorReplyAccepted,
  OperatorReplyRequest,
  PatchConversationEpisodeRequest,
  PatchEscalationRequest,
} from '../model/types';

const BASE = '/api/v1/conversations';

/** The operator queue: the hub's default listing shape (oldest waiting customer first). */
export const OPERATOR_QUEUE_PARAMS: ListConversationsParams = { status: 'WAITING_OPERATOR' };

/**
 * `GET /api/v1/conversations` — with no `status` the hub lists every thread; `status=WAITING_OPERATOR`
 * is the operator queue exactly as the hub orders it (the BFF does not re-sort).
 */
export const listConversations = async (
  params: ListConversationsParams = {},
): Promise<ConversationPage> => (await apiClient.get<ConversationPage>(BASE, { params })).data;

/**
 * `GET /api/v1/conversations/{conversationId}` — header, sessions, open escalation, context summary.
 * A MERGED alias answers with the SURVIVING thread's detail: compare `data.id` with the id you asked
 * for and follow it (adr-merchant-portal-021 D2).
 */
export const getConversation = async (conversationId: string): Promise<ConversationDetail> =>
  (await apiClient.get<ConversationDetail>(`${BASE}/${encodeURIComponent(conversationId)}`)).data;

/**
 * `GET …/messages` — the cross-channel timeline, newest first within the page; `nextBefore` is the
 * keyset cursor for the next OLDER page (`null` at the start of the thread). Inbound `text`,
 * `editedText` and `attachments[].fileName` are `null` without `customers.feature.pii-view`.
 */
export const listConversationMessages = async (
  conversationId: string,
  params: ListConversationMessagesParams = {},
): Promise<ConversationMessagePage> =>
  (
    await apiClient.get<ConversationMessagePage>(
      `${BASE}/${encodeURIComponent(conversationId)}/messages`,
      {
        params,
      },
    )
  ).data;

/**
 * `POST …/operator-replies` — `202` accepted, not sent; the delivery outcome arrives on the timeline.
 * `409 SESSION_NOT_FOUND` / `409 SESSION_UNREACHABLE` (+ `details.reachableChannels`) are the
 * composer's own error states, never a silent fallback to another channel.
 */
export const postOperatorReply = async (
  conversationId: string,
  body: OperatorReplyRequest,
): Promise<OperatorReplyAccepted> =>
  (
    await apiClient.post<OperatorReplyAccepted>(
      `${BASE}/${encodeURIComponent(conversationId)}/operator-replies`,
      body,
    )
  ).data;

/**
 * `PATCH …/escalations/{escalationId}` — `ACKED` (a person owns the episode) or `RESOLVED` (back to the
 * bot, the conversation returns to OPEN). Send `currentEpisode.version`; a stale one is
 * `409 settings.version_conflict`.
 */
export const patchEscalation = async (
  conversationId: string,
  escalationId: string,
  body: PatchEscalationRequest,
): Promise<ConversationEscalation> =>
  (
    await apiClient.patch<ConversationEscalation>(
      `${BASE}/${encodeURIComponent(conversationId)}/escalations/${encodeURIComponent(escalationId)}`,
      body,
    )
  ).data;

/**
 * `POST …:close` — closes the CURRENT episode (CLOSED is a state, not an ending: the next inbound
 * reopens the thread with a fresh episode). The body is optional; sending `version` makes it an
 * optimistic write (`409 settings.version_conflict` when stale).
 */
export const closeConversation = async (
  conversationId: string,
  body?: CloseConversationRequest,
): Promise<ConversationDetail> =>
  (
    await apiClient.post<ConversationDetail>(
      `${BASE}/${encodeURIComponent(conversationId)}:close`,
      body,
    )
  ).data;

/** `GET …/episodes` — episode history, newest first. */
export const listConversationEpisodes = async (
  conversationId: string,
  params: ListConversationEpisodesParams = {},
): Promise<ConversationEpisodePage> =>
  (
    await apiClient.get<ConversationEpisodePage>(
      `${BASE}/${encodeURIComponent(conversationId)}/episodes`,
      {
        params,
      },
    )
  ).data;

/** `PATCH …/episodes/{episodeId}` — rename the topic; `version` is the value last read (409 when stale). */
export const patchConversationEpisode = async (
  conversationId: string,
  episodeId: string,
  body: PatchConversationEpisodeRequest,
): Promise<ConversationEpisodeSummary> =>
  (
    await apiClient.patch<ConversationEpisodeSummary>(
      `${BASE}/${encodeURIComponent(conversationId)}/episodes/${encodeURIComponent(episodeId)}`,
      body,
    )
  ).data;

/**
 * `GET /api/v1/customers/{customerId}/conversation` — the customer's cross-channel conversation for
 * Customer 360. `404` when the customer has never talked to this tenant on any channel.
 */
export const getCustomerConversation = async (customerId: string): Promise<ConversationDetail> =>
  (
    await apiClient.get<ConversationDetail>(
      `/api/v1/customers/${encodeURIComponent(customerId)}/conversation`,
    )
  ).data;

/**
 * Resolves an attachment's `contentUrl` (a BFF-relative path such as
 * `/api/v1/conversations/{id}/attachments/{attachmentId}/content`) to the absolute URL an `<img src>`
 * or a download link can load under the OAuth2-BFF session cookie. Never a storage URL — the BFF
 * streams the bytes itself (`Cache-Control: private, no-store`). Returns `null` for a `null` path
 * (the attachment is not `STORED`).
 */
export function resolveAttachmentContentUrl(contentUrl: string | null | undefined): string | null {
  if (!contentUrl) {
    return null;
  }
  if (/^https?:\/\//i.test(contentUrl)) {
    return contentUrl;
  }
  const base = apiConfig.baseURL.replace(/\/+$/, '');
  return `${base}${contentUrl.startsWith('/') ? '' : '/'}${contentUrl}`;
}
